import { useEffect, useRef, useState } from 'react';

/*
 * Small pieces of feedback: numbers that count up to their new value and flash for a
 * moment. Without animations (reduced motion, or no matchMedia at all) they just change.
 */

export const reducedMotion = () =>
  typeof matchMedia !== 'function' || matchMedia('(prefers-reduced-motion: reduce)').matches;

/** The value shown while it counts from `from` (or the previous value) to `value`. */
function useCountUp(value: number, from?: number, ms = 700): number {
  const start = useRef(from ?? value);
  const [shown, setShown] = useState(from ?? value);
  useEffect(() => {
    const a = start.current;
    start.current = value;
    if (a === value || reducedMotion()) {
      setShown(value);
      return;
    }
    let frame = 0;
    const t0 = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / ms);
      // Fast at first, gentle at the end
      const eased = 1 - (1 - k) ** 3;
      setShown(Math.round(a + (value - a) * eased));
      if (k < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, ms]);
  return shown;
}

/**
 * A number that counts up to its new value; on a change it lights up for a moment, gold
 * going up and burgundy going down.
 */
export function AnimatedNumber({
  value,
  from,
  format = (n: number) => String(n),
}: {
  value: number;
  /** Count from here when it first appears (a delta counts from 0) */
  from?: number;
  format?: (n: number) => string;
}) {
  const shown = useCountUp(value, from);
  const last = useRef(value);
  const [flash, setFlash] = useState<'up' | 'down' | null>(null);
  useEffect(() => {
    if (value === last.current) return;
    setFlash(value > last.current ? 'up' : 'down');
    last.current = value;
    const t = setTimeout(() => setFlash(null), 900);
    return () => clearTimeout(t);
  }, [value]);
  return (
    <span
      className={`tabular rounded transition-colors duration-500 ${
        flash === 'up'
          ? 'text-gold-700 dark:text-gold-300'
          : flash === 'down'
            ? 'text-burgundy dark:text-burgundy-300'
            : ''
      }`}
    >
      {format(shown)}
    </span>
  );
}
